import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/redux/store'
import { fetchCoProfessors } from '@/redux/asyncThunks/room'
import type { CoProfessor } from '@/api/room'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface LeaveGameModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (newOwnerId?: number) => Promise<void>
  isOwner: boolean
  roomId: string
}

const LeaveGameModal: React.FC<LeaveGameModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isOwner,
  roomId,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const [coProfessors, setCoProfessors] = useState<CoProfessor[]>([])
  const [selectedNewOwnerId, setSelectedNewOwnerId] = useState<number | null>(null)
  const [isLoadingCoProfessors, setIsLoadingCoProfessors] = useState<boolean>(false)
  const [isConfirming, setIsConfirming] = useState<boolean>(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !isOwner) return

    setIsLoadingCoProfessors(true)
    setFetchError(null)
    setSelectedNewOwnerId(null)

    dispatch(fetchCoProfessors(roomId))
      .unwrap()
      .then((data) => setCoProfessors(data))
      .catch(() => setFetchError('Failed to load co-professors. Please try again.'))
      .finally(() => setIsLoadingCoProfessors(false))
  }, [isOpen, isOwner, roomId, dispatch])

  const handleClose = () => {
    if (isConfirming) return
    setSelectedNewOwnerId(null)
    setCoProfessors([])
    setFetchError(null)
    onClose()
  }

  const handleConfirm = async () => {
    setIsConfirming(true)
    try {
      await onConfirm(isOwner && selectedNewOwnerId !== null ? selectedNewOwnerId : undefined)
    } finally {
      setIsConfirming(false)
    }
  }

  const canConfirm =
    !isConfirming &&
    (!isOwner || (selectedNewOwnerId !== null && coProfessors.length > 0))

  const noCoProfessors = isOwner && !isLoadingCoProfessors && !fetchError && coProfessors.length === 0

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='sm:max-w-[460px]'>
        <DialogHeader>
          <DialogTitle>Leave Game Room</DialogTitle>
          <DialogDescription>
            {isOwner
              ? 'As the room owner, you must transfer ownership to a co-professor before leaving.'
              : 'Are you sure you want to leave this game room? You will lose access unless re-invited.'}
          </DialogDescription>
        </DialogHeader>

        {isOwner && (
          <div className='py-2'>
            {isLoadingCoProfessors && (
              <p className='text-sm text-slate-500'>Loading co-professors…</p>
            )}

            {fetchError && (
              <p className='text-sm text-red-600'>{fetchError}</p>
            )}

            {noCoProfessors && (
              <div className='p-3 bg-amber-50 border border-amber-200 rounded-md'>
                <p className='text-sm text-amber-800'>
                  There are no co-professors in this room. You cannot leave without
                  first inviting another professor.
                </p>
              </div>
            )}

            {coProfessors.length > 0 && (
              <div className='space-y-2'>
                <label className='text-sm font-medium text-slate-700'>
                  Transfer ownership to
                </label>
                <Select
                  onValueChange={(val) => setSelectedNewOwnerId(Number(val))}
                  disabled={isConfirming}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select a co-professor' />
                  </SelectTrigger>
                  <SelectContent>
                    {coProfessors.map((cp) => (
                      <SelectItem key={cp.id} value={String(cp.id)}>
                        <span className='block truncate max-w-[380px]'>
                          {cp.full_name} ({cp.email})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        <DialogFooter className='flex gap-2 sm:justify-end'>
          <Button
            type='button'
            variant='outline'
            onClick={handleClose}
            disabled={isConfirming}
            className='w-full sm:w-auto'
          >
            Cancel
          </Button>
          <Button
            type='button'
            variant='destructive'
            onClick={handleConfirm}
            disabled={!canConfirm || noCoProfessors || Boolean(fetchError)}
            className='w-full sm:w-auto'
          >
            {isConfirming ? 'Leaving…' : 'Leave Game'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default LeaveGameModal
